<template>
  <div class="select-wrapper">
    <div
      :class="selectClasses"
      @click="toggleDropdown"
      :tabindex="isDisabled ? -1 : 0"
      @keydown.enter.prevent="toggleDropdown"
      @keydown.space.prevent="toggleDropdown"
      @keydown.esc="closeDropdown"
      @keydown.down.prevent="navigateOptions(1)"
      @keydown.up.prevent="navigateOptions(-1)"
    >
      <span class="select__value" :class="{ 'select__value--placeholder': !displayValue }">
        {{ displayValue || placeholder }}
      </span>
      <OpenDownIcon
        class="select__icon"
        :color="iconColor"
      />
    </div>

    <transition name="dropdown">
      <div v-if="isOpen" class="select__dropdown">
        <div
          v-for="(option, index) in options"
          :key="getOptionValue(option)"
          class="select__option"
          :class="{
            'is-selected': isSelected(option),
            'is-focused': index === focusedIndex
          }"
          @click="selectOption(getOptionValue(option))"
          @mouseenter="focusedIndex = index"
        >
          <slot name="option" :option="option">
            {{ getOptionLabel(option) }}
          </slot>
        </div>
        <div v-if="options.length === 0" class="select__empty">
          {{ emptyText }}
        </div>
      </div>
    </transition>

    <div v-if="hasError" class="select--error">
      <div v-for="(error, index) in errorMessages" :key="index">
        {{ error }}
      </div>
    </div>
  </div>
</template>

<script>
import { clsx } from 'clsx';
import { OpenDownIcon } from '@/shared/ui/icons';

export default {
  components: { OpenDownIcon },
  model: {
    prop: 'value',
    event: 'input'
  },
  props: {
    value: {
      type: [String, Number, Object],
      default: null
    },
    options: {
      type: Array,
      required: true
    },
    placeholder: {
      type: String,
      default: 'Select option'
    },
    isDisabled: {
      type: Boolean,
      default: false
    },
    valueKey: {
      type: String,
      default: 'value'
    },
    labelKey: {
      type: String,
      default: 'label'
    },
    iconColor: {
      type: String,
      default: '#09090B',
    },
    emptyText: {
      type: String,
      default: 'No options available'
    },
    errorMessages: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      isOpen: false,
      focusedIndex: -1
    };
  },
  computed: {
    selectClasses() {
      return clsx('select', {
        'select--disabled': this.isDisabled,
        'select--has-error': this.hasError,
        'select--open': this.isOpen
      });
    },
    displayValue() {
      if (!this.value) return '';

      const selected = this.options.find(opt =>
        this.getOptionValue(opt) === this.getOptionValue(this.value)
      );

      return selected ? this.getOptionLabel(selected) : '';
    },
    hasError() {
      return this.errorMessages.length > 0;
    }
  },
  methods: {
    toggleDropdown() {
      if (this.isDisabled) return;
      this.isOpen = !this.isOpen;
      if (this.isOpen) {
        this.focusedIndex = this.options.findIndex(opt => this.isSelected(opt));
      }
    },
    closeDropdown() {
      this.isOpen = false;
      this.focusedIndex = -1;
    },
    selectOption(option) {
      this.$emit('input', option);
      this.$emit('change', option);
      this.closeDropdown();
    },
    getOptionValue(option) {
      return typeof option === 'object' ? option[this.valueKey] : option;
    },
    getOptionLabel(option) {
      return typeof option === 'object' ? option[this.labelKey] : option;
    },
    isSelected(option) {
      if (!this.value) return false;
      return this.getOptionValue(option) === this.getOptionValue(this.value);
    },
    navigateOptions(direction) {
      if (!this.isOpen) {
        this.isOpen = true;
        return;
      }
      const newIndex = this.focusedIndex + direction;
      if (newIndex >= 0 && newIndex < this.options.length) {
        this.focusedIndex = newIndex;
      }

      if (this.focusedIndex >= 0) {
        this.selectOption(this.options[this.focusedIndex]);
      }
    },
    handleClickOutside(event) {
      if (!this.$el.contains(event.target)) {
        this.closeDropdown();
      }
    }
  },
  mounted() {
    document.addEventListener('click', this.handleClickOutside);
  },
  beforeDestroy() {
    document.removeEventListener('click', this.handleClickOutside);
  }
};
</script>

<style lang="scss" scoped>
.select-wrapper {
  position: relative;
  width: 100%;
}

.select {
  position: relative;
  background-color: #ffffff;
  border: 1px solid #E4E4E7;
  border-radius: 6px;
  padding: 8px 12px;
  margin-bottom: 4px;
  width: 100%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: space-between;

  &--disabled {
    background-color: #F6F6F9;
    cursor: not-allowed;
    opacity: 0.6;
  }

  &--has-error {
    border-color: #f00;
  }

  &__value {
    flex: 1;
    color: #09090B;
    font-size: 14px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &__icon {
    margin-left: 8px;
    transition: transform 0.2s;
  }

  &--open &__icon {
    transform: rotate(180deg);
  }

  &__dropdown {
    position: absolute;
    max-height: 300px;
    overflow-y: auto;
    background-color: #ffffff;
    border: 1px solid #E4E4E7;
    border-radius: 6px;
    width: 100%;
    box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1),
    0 4px 6px -2px rgba(0, 0, 0, 0.05);
    z-index: 1000;

    &::-webkit-scrollbar {
      width: 6px;
    }

    &::-webkit-scrollbar-track {
      background: #f3f4f6;
    }

    &::-webkit-scrollbar-thumb {
      background: #d1d5db;
      border-radius: 3px;

      &:hover {
        background: #9ca3af;
      }
    }
  }

  &__option {
    padding: 10px 16px;
    cursor: pointer;
    transition: background-color 0.15s ease;
    font-size: 14px;
    color: #09090B;

    &:hover,
    &.is-focused {
      background-color: #f3f4f6;
    }

    &.is-selected {
      background-color: #eff6ff;
      color: #3b82f6;
      font-weight: 500;
    }
  }

  &__empty {
    padding: 16px;
    text-align: center;
    color: #9ca3af;
    font-size: 14px;
  }

  &--error {
    font-family: 'Inter', sans-serif;
    font-size: 12px;
    color: #f00;
    margin-top: 4px;
    margin-bottom: 12px;
  }
}

.dropdown-enter-active,
.dropdown-leave-active {
  transition: opacity 0.2s ease, transform 0.2s ease;
}

.dropdown-enter,
.dropdown-leave-to {
  opacity: 0;
  transform: translateY(-8px);
}
</style>
